import React, { useState } from 'react';
import SEO from '../../components/SEO';
import { generateArticle } from '../../utils/structuredData';
import seoData from '../../config/seoData';
import { MessageCircle, Instagram, Menu, X, ArrowLeft, Calendar, Clock, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useNavigate, Link } from 'react-router-dom';

const RemoverCimentoArgamassa = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Olá! Vi o artigo sobre remoção de cimento e gostaria de ajuda.');
    window.open(`https://wa.me/5531971823249?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen
      <SEO 
        title={seoData['/blog/remover-cimento-argamassa'].title}
        description={seoData['/blog/remover-cimento-argamassa'].description}
        canonical={seoData['/blog/remover-cimento-argamassa'].canonical}
      /> bg-white">
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
              alt="WellTec Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">Serviços</Link>
            <Link to="/limpeza-pos-obra" className="hover:text-blue-400 transition-colors">Limpeza Pós-Obra</Link>
            <Link to="/blog" className="text-green-400 font-semibold">Blog</Link>
            <a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>

          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded">Início</button>
              <button onClick={() => { navigate('/servicos'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded">Serviços</button>
              <button onClick={() => { navigate('/limpeza-pos-obra'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded">Limpeza Pós-Obra</button>
              <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded text-green-400 font-semibold">Blog</button>
              <Button onClick={() => { handleWhatsAppClick(); setMobileMenuOpen(false); }} className="bg-green-500 hover:bg-green-600 w-full mt-2">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        )}
      </header>

      <article className="py-12 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <button onClick={() => navigate('/blog')} className="flex items-center text-blue-600 hover:text-blue-800 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para o Blog
          </button>

          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Como Remover Resíduos de Cimento e Argamassa Após Reforma
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-gray-600 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Janeiro 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>8 min de leitura</span>
              </div>
            </div>
          </header>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
              Resíduos de <strong>cimento e argamassa</strong> são os mais difíceis de remover após uma obra. Se não forem tratados corretamente, podem manchar permanentemente pisos e revestimentos. Veja como fazer a remoção profissional.
            </p>

            <Card className="bg-red-50 border-2 border-red-200 mb-6">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-8 h-8 text-red-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">⚠️ ATENÇÃO: Risco de Danos Permanentes</h3>
                    <p className="text-gray-700">
                      Usar produtos errados pode manchar ou corroer pisos de porcelanato, mármore, granito e outros materiais. Siga sempre as técnicas corretas para cada tipo de superfície.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Tipos de Resíduos e Como Identificar
            </h2>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              1. Cimento Fresco (até 24h)
            </h3>
            <p className="text-gray-700 mb-4">
              <strong>Características:</strong> Ainda úmido ou semi-seco, coloração cinza clara, fácil de riscar com unha.
            </p>
            <p className="text-gray-700 mb-6">
              <strong>Remoção:</strong> Use água quente + detergente neutro + escova de nylon. É o momento mais fácil para remover.
            </p>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              2. Cimento Seco (mais de 48h)
            </h3>
            <p className="text-gray-700 mb-4">
              <strong>Características:</strong> Endurecido, aderido à superfície, difícil de raspar.
            </p>
            <p className="text-gray-700 mb-6">
              <strong>Remoção:</strong> Exige produtos ácidos específicos (removedores de cimento).
            </p>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              3. Argamassa e Rejunte
            </h3>
            <p className="text-gray-700 mb-6">
              <strong>Remoção:</strong> Removedores específicos para rejunte + raspagem cuidadosa.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Produtos Profissionais para Remoção
            </h2>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-6">
              <h4 className="font-bold text-gray-900 mb-4">Removedores Ácidos:</h4>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Ácido muriático diluído:</strong> Para pisos resistentes (cerâmica comum)</li>
                <li><strong>Removedor de cimento específico:</strong> Para porcelanato e pisos delicados</li>
                <li><strong>Removedor de rejunte:</strong> Para excesso em azulejos</li>
              </ul>

              <h4 className="font-bold text-gray-900 mb-4 mt-6">Ferramentas Necessárias:</h4>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Espátula de plástico ou metal (conforme superfície)</li>
                <li>Escovas de nylon de diferentes tamanhos</li>
                <li>Panos de microfibra</li>
                <li>Luvas de borracha grossas</li>
                <li>Óculos de proteção</li>
                <li>Máscara respiratória</li>
              </ul>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Passo a Passo: Remoção Profissional
            </h2>

            <Card className="bg-blue-50 border-2 border-blue-200 mb-6">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Passo 1: Proteção</h4>
                    <p className="text-gray-700">Vista luvas, óculos e máscara. Abra janelas para ventilação.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Passo 2: Teste em Área Pequena</h4>
                    <p className="text-gray-700">Sempre teste o produto em um canto discreto antes de aplicar em toda área.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Passo 3: Aplicação do Removedor</h4>
                    <p className="text-gray-700">Aplique o produto diluído conforme instruções do fabricante. Deixe agir por 5-10 minutos (não deixe secar).</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Passo 4: Raspagem Cuidadosa</h4>
                    <p className="text-gray-700">Use espátula adequada ao material. Raspe em ângulo de 45º para não arranhar.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Passo 5: Escovação</h4>
                    <p className="text-gray-700">Esfregue com escova de nylon em movimentos circulares.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Passo 6: Enxágue Abundante</h4>
                    <p className="text-gray-700">Lave com muita água limpa para neutralizar o ácido.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Passo 7: Secagem</h4>
                    <p className="text-gray-700">Seque completamente com pano absorvente.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Passo 8: Conferência</h4>
                    <p className="text-gray-700">Repita o processo em áreas com resíduos remanescentes.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Cuidados Por Tipo de Piso
            </h2>

            <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden mb-6">
              <table className="w-full">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="p-4 text-left">Tipo de Piso</th>
                    <th className="p-4 text-left">Produto Permitido</th>
                    <th className="p-4 text-left">Produto PROIBIDO</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Porcelanato</td>
                    <td className="p-4">Removedor específico diluído</td>
                    <td className="p-4 text-red-600">Ácido puro, palha de aço</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Cerâmica</td>
                    <td className="p-4">Ácido muriático diluído 1:10</td>
                    <td className="p-4 text-red-600">Ácido concentrado</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Mármore/Granito</td>
                    <td className="p-4">Removedor neutro específico</td>
                    <td className="p-4 text-red-600">Qualquer ácido</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Madeira</td>
                    <td className="p-4">Raspagem delicada + pano úmido</td>
                    <td className="p-4 text-red-600">Produtos químicos</td>
                  </tr>
                  <tr>
                    <td className="p-4 font-semibold">Piso Industrial</td>
                    <td className="p-4">Removedores industriais</td>
                    <td className="p-4 text-red-600">-</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Erros Comuns que Causam Manchas Permanentes
            </h2>

            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Usar ácido muriático puro em porcelanato</li>
              <li>Deixar produto ácido secar sobre o piso</li>
              <li>Usar palha de aço em superfícies polidas</li>
              <li>Não enxaguar suficientemente após aplicação</li>
              <li>Raspar com espátula muito afiada</li>
              <li>Usar produtos inadequados para o tipo de material</li>
            </ul>

            <Card className="bg-yellow-50 border-2 border-yellow-200 mb-6">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-3">💡 Dica Profissional</h3>
                <p className="text-gray-700">
                  Para porcelanato e materiais delicados, SEMPRE dilua o removedor mais do que o indicado na primeira aplicação. É melhor repetir o processo várias vezes com produto mais fraco do que manchar permanentemente.
                </p>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Quando Contratar um Profissional?
            </h2>
            <p className="text-gray-700 mb-4">
              Recomenda-se contratar profissionais quando:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>O piso é de material delicado (mármore, granito, porcelanato polido)</li>
              <li>A área afetada é muito grande (mais de 50m²)</li>
              <li>Os resíduos estão há muito tempo (mais de 1 semana)</li>
              <li>Você não tem experiência com produtos químicos</li>
              <li>Tentativas anteriores falharam ou causaram manchas</li>
            </ul>

            <Card className="bg-blue-900 text-white mb-6">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Precisa Remover Resíduos de Cimento?</h3>
                <p className="mb-6">
                  A WellTec possui produtos profissionais e técnicos especializados para remover resíduos sem danificar seu piso
                </p>
                <Button 
                  onClick={handleWhatsAppClick}
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-6"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Solicitar Remoção Profissional
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Artigos Relacionados</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/blog/limpeza-pos-obra-residencial')}>
                <CardContent className="p-6">
                  <h4 className="font-bold text-gray-900 mb-2">Checklist Limpeza Pós-Obra</h4>
                  <p className="text-gray-600 text-sm">Tudo que deve ser limpo após reforma</p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/blog/limpeza-vs-convencional')}>
                <CardContent className="p-6">
                  <h4 className="font-bold text-gray-900 mb-2">Limpeza Pós-Obra vs Convencional</h4>
                  <p className="text-gray-600 text-sm">Entenda as diferenças técnicas</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </article>

      <footer className="bg-slate-900 text-white py-12 mt-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="font-bold mb-4">WellTec</h3>
              <p className="text-gray-400 text-sm">Limpeza pós-obra profissional em BH e região</p>
            </div>
            <div>
              <h3 className="font-bold mb-4">Serviços</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/limpeza-pos-obra" className="hover:text-green-400">Limpeza Pós-Obra</Link></li>
                <li><Link to="/locacao-lavadora-piso" className="hover:text-green-400">Locação de Equipamentos</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Contato</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="tel:+5531971823249" className="hover:text-green-400">(31) 97182-3249</a></li>
                <li><button onClick={handleWhatsAppClick} className="hover:text-green-400">WhatsApp</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} WellTec. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

      <button 
        onClick={handleWhatsAppClick}
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all z-50"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    </div>
  );
};

export default RemoverCimentoArgamassa;
